// ============================================================
// caseClaim.ts — the case's CLAIM group on the frontend (B2; pure:
// caseClaim.test.ts). Reads the B1 columns off a case response into
// Case.claim, hands them to the limitation estimate, and holds the client
// half of the "set the limitation date myself" rule (the server repeats it —
// backend/src/lib/caseClaim.ts).
// ============================================================
import { CLAIM_TYPE_LABELS, TYPE_SPECIFIC_FIELDS, TYPE_SPECIFIC_FIELD_LABELS, claimFieldApplies, frcSuggested, isAbuseType, isClaimRoute, isClaimTrack, isClaimType, isHolidayRegime, routeAllowedFor, routeNotForTypeMessage, type TypeSpecificField } from './claimTypes'
import { ukDate } from './civilDate'
import { militaryLongstop, ownDateOverLongstop, type LimitationClaimFacts } from './limitationRules'
import type { CaseClaim, RawCase } from '../types/case.types'

// DATE columns arrive as ISO timestamps (UTC midnight) → "YYYY-MM-DD".
const dayOf = (v: unknown): string | null => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null)

// Any case-shaped object: a RawCase response, a list row, or the PDF's flat
// export (where the dates are already YYYY-MM-DD).
export const claimOfRaw = (raw: object): CaseClaim => {
  const d = raw as Partial<Record<keyof RawCase, unknown>>
  return {
    claimType: isClaimType(d.claimType) ? d.claimType : null,
    claimRoute: isClaimRoute(d.claimRoute) ? d.claimRoute : null,
    track: isClaimTrack(d.track) ? d.track : null,
    complexityBand: typeof d.complexityBand === 'number' ? d.complexityBand : null,
    frcRegime: typeof d.frcRegime === 'boolean' ? d.frcRegime : null,
    dateOfKnowledge: dayOf(d.dateOfKnowledge),
    dateOfDeath: dayOf(d.dateOfDeath),
    limitationOverrideDate: dayOf(d.limitationOverrideDate),
    limitationOverrideReason: typeof d.limitationOverrideReason === 'string' ? d.limitationOverrideReason : '',
    holidayRegime: isHolidayRegime(d.holidayRegime) ? d.holidayRegime : null,
    travelArrivalDate: dayOf(d.travelArrivalDate),
    foreignLimitationMonths: typeof d.foreignLimitationMonths === 'number' ? d.foreignLimitationMonths : null,
    abuseType: isAbuseType(d.abuseType) ? d.abuseType : null,
    policeFirstReportedAt: dayOf(d.policeFirstReportedAt),
    overseasOperation: typeof d.overseasOperation === 'boolean' ? d.overseasOperation : null,
    firstSymptomsDate: dayOf(d.firstSymptomsDate),
    diagnosisDate: dayOf(d.diagnosisDate),
    abuseNoLimitConfirmedAt: typeof d.abuseNoLimitConfirmedAt === 'string' && d.abuseNoLimitConfirmedAt ? d.abuseNoLimitConfirmedAt : null,
    abuseNoLimitConfirmedByUserId: typeof d.abuseNoLimitConfirmedByUserId === 'string' ? d.abuseNoLimitConfirmedByUserId : null,
  }
}

// The estimate's input (utils/limitationRules estimatedLimitation).
// protectedParty = PiMatter.protectedParty (the capacity note).
export const claimFactsOf = (claim: CaseClaim | null | undefined, protectedParty?: boolean | null): LimitationClaimFacts => ({
  claimType: claim?.claimType ?? null,
  dateOfKnowledge: claim?.dateOfKnowledge ?? null,
  dateOfDeath: claim?.dateOfDeath ?? null,
  overrideDate: claim?.limitationOverrideDate ?? null,
  protectedParty: protectedParty === true,
  claimRoute: claim?.claimRoute ?? null,
  holidayRegime: claim?.holidayRegime ?? null,
  travelArrivalDate: claim?.travelArrivalDate ?? null,
  foreignLimitationMonths: claim?.foreignLimitationMonths ?? null,
  abuseType: claim?.abuseType ?? null,
  abuseNoLimitConfirmedAt: claim?.abuseNoLimitConfirmedAt ?? null,
  policeFirstReportedAt: claim?.policeFirstReportedAt ?? null,
  overseasOperation: claim?.overseasOperation ?? null,
  firstSymptomsDate: claim?.firstSymptomsDate ?? null,
  diagnosisDate: claim?.diagnosisDate ?? null,
})

// "Set limitation date myself": a date needs a reason (same rule + wording
// as the server's OVERRIDE_REASON_REQUIRED).
export const OVERRIDE_REASON_REQUIRED = 'Give a reason for setting the limitation date yourself.'
export const overrideReasonError = (date: string | null | undefined, reason: string | null | undefined): string | null =>
  date && !(reason ?? '').trim() ? OVERRIDE_REASON_REQUIRED : null

// ── The Claim card's DRAFT (Audit fix batch 1) ─────────────────────────
// The card edits a draft and saves it ONLY on Save, all fields in ONE request
// (components/sections/ClaimCard). These pure rules say what blocks Save
// (errors, red) and what's only flagged (warnings, amber), and what is sent.
// Dates "YYYY-MM-DD"; today = the London day.
export type ClaimDraft = CaseClaim & { ownOn: boolean } // ownOn = "Set limitation date myself" ticked
export const draftFromClaim = (c: CaseClaim): ClaimDraft => ({ ...c, ownOn: !!c.limitationOverrideDate })

export const CLAIM_TYPE_MISSING = 'Choose a claim type.'
export const DATE_IN_FUTURE = 'Can’t be in the future'
export const ownDateBeforeAccident = (accidentIso: string): string => `Can’t be before the accident date (${ukDate(accidentIso)})`
export const OWN_DATE_MISSING = 'Enter the limitation date, or untick the box.'
export const REASON_MAX = 1000
export const REASON_TOO_LONG = `Keep the reason under ${REASON_MAX} characters.`
// (B2b) The foreign limitation period (months) — the server's rule too.
export const FOREIGN_MONTHS_MIN = 1
export const FOREIGN_MONTHS_MAX = 120
export const FOREIGN_MONTHS_INVALID = 'Enter a whole number of months, 1 to 120.'

export type ClaimDraftErrors = Partial<Record<keyof CaseClaim, string>>
// Anything here BLOCKS Save (the server refuses the same).
// (Audit fix 1, M1) The server refuses these too (backend lib/caseClaim
// claimDateOrderError) — same wording.
export const REPORT_BEFORE_INCIDENT = 'The date first reported to police can’t be before the incident date.'
export const ARRIVAL_BEFORE_ACCIDENT = 'The arrival date can’t be before the accident date.'
// ctx.saved — the case's saved type + route: as the server, a route that
// doesn't fit is refused only when the type or the route is being changed
// (an older saved mismatch doesn't block an unrelated save). Absent = new.
export const claimDraftErrors = (d: ClaimDraft, ctx: { accidentIso: string | null; today: string; saved?: { claimType: string | null; claimRoute: string | null } }): ClaimDraftErrors => {
  const e: ClaimDraftErrors = {}
  if (!d.claimType) e.claimType = CLAIM_TYPE_MISSING
  // (29 Oct) The route must fit the claim type (claimTypes ROUTES_BY_TYPE).
  const routeTouched = !ctx.saved || (d.claimType ?? null) !== (ctx.saved.claimType ?? null) || (d.claimRoute ?? null) !== (ctx.saved.claimRoute ?? null)
  if (routeTouched && !routeAllowedFor(d.claimRoute, d.claimType)) e.claimRoute = routeNotForTypeMessage(d.claimRoute, d.claimType)
  if (d.dateOfKnowledge && d.dateOfKnowledge > ctx.today) e.dateOfKnowledge = DATE_IN_FUTURE
  // (B2b fix 2) A field hidden for the current type (it'll be cleared on
  // Save) never blocks Save.
  const on = (k: TypeSpecificField): boolean => claimFieldApplies(k, d)
  if (on('dateOfDeath') && d.dateOfDeath && d.dateOfDeath > ctx.today) e.dateOfDeath = DATE_IN_FUTURE
  // (B2b) The new dates — none can be in the future.
  for (const k of ['travelArrivalDate', 'policeFirstReportedAt', 'firstSymptomsDate', 'diagnosisDate'] as const) {
    const v = d[k]
    if (on(k) && v && v > ctx.today) e[k] = DATE_IN_FUTURE
  }
  // (Audit fix 1, M1) A typo that would move the limitation estimate.
  if (ctx.accidentIso && on('policeFirstReportedAt') && d.policeFirstReportedAt && !e.policeFirstReportedAt && d.policeFirstReportedAt < ctx.accidentIso) e.policeFirstReportedAt = REPORT_BEFORE_INCIDENT
  if (ctx.accidentIso && on('travelArrivalDate') && d.travelArrivalDate && !e.travelArrivalDate && d.travelArrivalDate < ctx.accidentIso) e.travelArrivalDate = ARRIVAL_BEFORE_ACCIDENT
  const m = d.foreignLimitationMonths
  if (on('foreignLimitationMonths') && m !== null && !(Number.isInteger(m) && m >= FOREIGN_MONTHS_MIN && m <= FOREIGN_MONTHS_MAX)) e.foreignLimitationMonths = FOREIGN_MONTHS_INVALID
  if (d.ownOn) {
    // (B2b) Military + overseas operation: never later than the longstop (RED).
    const cap = militaryLongstop(ctx.accidentIso, claimFactsOf(d))
    if (!d.limitationOverrideDate) e.limitationOverrideDate = OWN_DATE_MISSING
    else if (ctx.accidentIso && d.limitationOverrideDate < ctx.accidentIso) e.limitationOverrideDate = ownDateBeforeAccident(ctx.accidentIso)
    else if (cap && d.limitationOverrideDate > cap.ymd) e.limitationOverrideDate = ownDateOverLongstop(cap.ymd)
    const reason = d.limitationOverrideReason.trim()
    if (d.limitationOverrideDate && !reason) e.limitationOverrideReason = OVERRIDE_REASON_REQUIRED
    else if (reason.length > REASON_MAX) e.limitationOverrideReason = REASON_TOO_LONG
  }
  return e
}

// SOFT warnings (amber; Save still allowed).
export const OWN_DATE_PASSED = 'This date has already passed'
export const ownDateLaterThanEstimate = (estimateYmd: string): string =>
  `This is later than Casevo’s estimate (${ukDate(estimateYmd)}). Make sure the reason explains why.`
export const DEATH_BEFORE_ACCIDENT = 'The date of death is before the accident date. Check it.'
export const DEATH_BEFORE_BIRTH = 'The date of death is before the date of birth. Check it.'
// (Audit fix 1) More soft warnings.
export const ARRIVAL_LATE = 'Check the arrival date. It’s more than 7 days after the accident.'
export const DISEASE_DATE_BEFORE_ACCIDENT = 'This is before the accident date, so the limitation estimate runs from it. Check both dates.'
export const DIED_NOT_FATAL = 'Client has died. Consider changing the claim type to Fatal (limitation can run from the date of death).'
const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000)
export interface ClaimDraftWarnings {
  limitationOverrideDate: string[]
  dateOfDeath: string[]
  travelArrivalDate: string[]
  firstSymptomsDate: string[]
  diagnosisDate: string[]
  dateOfKnowledge: string[]
}
export const NO_CLAIM_WARNINGS: ClaimDraftWarnings = { limitationOverrideDate: [], dateOfDeath: [], travelArrivalDate: [], firstSymptomsDate: [], diagnosisDate: [], dateOfKnowledge: [] }
export const claimDraftWarnings = (
  d: ClaimDraft,
  ctx: { accidentIso: string | null; dobIso: string | null; today: string; estimateYmd: string | null },
): ClaimDraftWarnings => {
  const own: string[] = []
  const date = d.ownOn ? d.limitationOverrideDate : null
  if (date) {
    if (date < ctx.today) own.push(OWN_DATE_PASSED)
    if (ctx.estimateYmd && date > ctx.estimateYmd) own.push(ownDateLaterThanEstimate(ctx.estimateYmd))
  }
  const death: string[] = []
  if (d.dateOfDeath) {
    if (ctx.accidentIso && d.dateOfDeath < ctx.accidentIso) death.push(DEATH_BEFORE_ACCIDENT)
    if (ctx.dobIso && d.dateOfDeath < ctx.dobIso) death.push(DEATH_BEFORE_BIRTH)
    if (d.claimType !== 'FATAL' && claimFieldApplies('dateOfDeath', d)) death.push(DIED_NOT_FATAL)
  }
  const acc = ctx.accidentIso
  const arrival: string[] = []
  if (acc && claimFieldApplies('travelArrivalDate', d) && d.travelArrivalDate && d.travelArrivalDate >= acc && daysBetween(acc, d.travelArrivalDate) > 7) arrival.push(ARRIVAL_LATE)
  // (Audit fix 1, H2) A disease date before the accident → the estimate runs from it.
  const before = (v: string | null, applies: boolean): string[] => (acc && applies && v && v < acc ? [DISEASE_DATE_BEFORE_ACCIDENT] : [])
  const disease = claimFieldApplies('firstSymptomsDate', d)
  return {
    limitationOverrideDate: own,
    dateOfDeath: death,
    travelArrivalDate: arrival,
    firstSymptomsDate: before(d.firstSymptomsDate, disease),
    diagnosisDate: before(d.diagnosisDate, disease),
    dateOfKnowledge: before(d.dateOfKnowledge, d.claimType === 'EL_DISEASE'),
  }
}

// What Save sends: every claim field that changed, and — while the own date
// is ticked — ALWAYS its date + reason together (the server then recreates a
// deleted Limitation deadline and keeps the deadline's note on the reason).
// Unticked when one was saved → both cleared. Empty → nothing to send.
const PLAIN_FIELDS: (keyof CaseClaim)[] = [
  'claimType', 'claimRoute', 'track', 'complexityBand', 'frcRegime', 'dateOfKnowledge', 'dateOfDeath',
  // (B2b) — the confirmation (abuseNoLimitConfirmed*) is never sent by Save.
  'holidayRegime', 'travelArrivalDate', 'foreignLimitationMonths', 'abuseType', 'policeFirstReportedAt',
  'overseasOperation', 'firstSymptomsDate', 'diagnosisDate',
]
export const claimSavePayload = (saved: CaseClaim, d: ClaimDraft): Record<string, unknown> => {
  const body: Record<string, unknown> = {}
  for (const k of PLAIN_FIELDS) if (d[k] !== saved[k]) body[k] = d[k]
  // (B2b fix 2) What no longer applies to the new type / regime is cleared
  // (the server does the same — lib/caseClaim clearNotApplicable).
  for (const k of clearedByDraft(saved, d)) body[k] = null
  if (d.ownOn && d.limitationOverrideDate) {
    body.limitationOverrideDate = d.limitationOverrideDate
    body.limitationOverrideReason = d.limitationOverrideReason.trim()
  } else if (saved.limitationOverrideDate) {
    body.limitationOverrideDate = null
    body.limitationOverrideReason = ''
  }
  return body
}

// Fixed recoverable costs suggestion: from the accident date (on/after
// 1 Oct 2023 → Yes) — EXCEPT disease claims, where it turns on the letter of
// claim date, so nothing is suggested (a hint says why).
export const FRC_DISEASE_HINT = 'For disease claims this depends on the letter of claim date (on or after 1 Oct 2023).'
// (B2b fix 2) Nor for CICA (not a court claim) or clinical negligence (not
// in force yet) — utils/claimTypes claimFieldApplies.
export const frcSuggestionFor = (claimType: string | null | undefined, accidentIso: string | null | undefined): boolean | null =>
  claimType === 'EL_DISEASE' || !claimFieldApplies('frcRegime', { claimType }) ? null : frcSuggested(accidentIso)
export const FRC_NOT_YET = 'Not applicable yet'
export const FRC_CLIN_NEG_HINT = 'Fixed recoverable costs aren’t in force for clinical negligence yet.'

// ── Changing the claim type (B2b fix 2) ────────────────────────────
// The saved type-specific values the DRAFT's type / holiday regime no longer
// uses — cleared on Save. Listed above Save: "Changing to [type] will clear: …".
export const clearedByDraft = (saved: CaseClaim, d: CaseClaim): TypeSpecificField[] =>
  TYPE_SPECIFIC_FIELDS.filter((k) => saved[k] !== null && saved[k] !== undefined && !claimFieldApplies(k, d))
export const clearLine = (saved: CaseClaim, d: CaseClaim): string | null => {
  const fields = clearedByDraft(saved, d)
  if (fields.length === 0) return null
  const names = fields.map((k) => TYPE_SPECIFIC_FIELD_LABELS[k])
  if (fields.includes('abuseType') && saved.abuseNoLimitConfirmedAt) names.push('the “no time limit” confirmation')
  const what = d.claimType && d.claimType !== saved.claimType ? `Changing to ${CLAIM_TYPE_LABELS[d.claimType]}` : 'Changing the type of holiday claim'
  return `${what} will clear: ${names.join(', ')}.`
}

// (B2b follow-up) Who may confirm / remove the child sexual abuse "no time
// limit": the case's fee earner (owner, or on its handling team) or a firm
// admin. The server's rule too (backend lib/abuseNoLimit canConfirmNoTimeLimit
// — a parity test keeps them the same).
export const ABUSE_CONFIRM_FORBIDDEN = 'Only the fee earner or an admin can confirm this.'
export const canConfirmNoTimeLimit = (
  user: { userId: string; role: string },
  c: { assignedToUserId: string | null; assignees?: { userId: string }[] | null },
): boolean => user.role === 'admin' || c.assignedToUserId === user.userId || (c.assignees ?? []).some((a) => a.userId === user.userId)

// Date of death shows only for a fatal claim, or when one is already saved.
// (B2b fix 2) Only for a fatal claim (changing away clears it).
export const showDateOfDeath = (claim: CaseClaim | null | undefined): boolean =>
  claimFieldApplies('dateOfDeath', { claimType: claim?.claimType })

// (B2b) Which of the new limitation inputs the Claim card shows — (B2b fix 2)
// ONLY the ones for the current type / holiday regime (utils/claimTypes
// claimFieldApplies); a value left from an earlier type is hidden and
// cleared when the type is saved.
export interface ClaimExtraFields {
  holidayRegime: boolean
  travelArrivalDate: boolean
  foreignLimitationMonths: boolean
  abuseType: boolean
  policeFirstReportedAt: boolean
  overseasOperation: boolean
  diseaseDates: boolean
}
export const claimExtraFields = (c: CaseClaim | null | undefined): ClaimExtraFields => {
  const f = { claimType: c?.claimType ?? null, holidayRegime: c?.holidayRegime ?? null }
  return {
    holidayRegime: claimFieldApplies('holidayRegime', f),
    travelArrivalDate: claimFieldApplies('travelArrivalDate', f),
    foreignLimitationMonths: claimFieldApplies('foreignLimitationMonths', f),
    abuseType: claimFieldApplies('abuseType', f),
    policeFirstReportedAt: claimFieldApplies('policeFirstReportedAt', f),
    overseasOperation: claimFieldApplies('overseasOperation', f),
    diseaseDates: claimFieldApplies('firstSymptomsDate', f),
  }
}
